if (!customElements.get('media-grid-custom')) {
  class MediaGridCustom extends HTMLElement {
    constructor() {
      super();
      this.videos = [];
      this.mediaItems = [];
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      this.initVideos();
      this.setupIntersectionObserver();
      this.initParallax();
    }

    initParallax() {
      // Disable parallax on mobile devices (screen width < 750px)
      if (window.innerWidth < 750) {
        return;
      }

      // Get all media items for parallax effect
      this.mediaItems = Array.from(this.querySelectorAll('.media-grid__item'));

      // Add scroll listener with throttling for performance
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });

      // Initial parallax calculation
      this.handleScroll();
    }

    handleScroll() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      this.mediaItems.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemTop = rect.top + scrollY;
        const itemHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Only apply parallax when item is near viewport
        if (rect.top < windowHeight && rect.bottom > 0) {
          // Calculate parallax offset based on scroll position
          // Alternating direction for staggered effect
          const isEven = index % 2 === 0;
          const speed = isEven ? 0.03 : -0.03; // Very subtle parallax speed
          const offset = (scrollY - itemTop + windowHeight) * speed;

          // Apply transform
          const mediaWrapper = item.querySelector('.media-grid__media-wrapper');
          if (mediaWrapper) {
            mediaWrapper.style.transform = `translateY(${offset}px)`;
          }
        }
      });
    }

    initVideos() {
      this.videos = Array.from(this.querySelectorAll('.media-grid__video'));

      this.videos.forEach((video) => {
        // Ensure videos are muted and set to loop
        video.muted = true;
        video.loop = true;
        video.playsInline = true;

        // Try to play the video
        this.playVideo(video);

        // Handle video errors
        video.addEventListener('error', (e) => {
          console.error('Video loading error:', e);
        });

        // Pause video when not in viewport to save resources
        video.addEventListener('loadedmetadata', () => {
          // Video metadata loaded successfully
        });
      });
    }

    playVideo(video) {
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video is playing successfully
          })
          .catch((error) => {
            // Auto-play was prevented
            console.warn('Video autoplay prevented:', error);

            // Try to play again on user interaction
            document.addEventListener('click', () => {
              video.play().catch(() => {
                // Still couldn't play
              });
            }, { once: true });
          });
      }
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.25
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting) {
            // Video is in viewport - play it
            if (video.paused) {
              this.playVideo(video);
            }
          } else {
            // Video is out of viewport - pause it to save resources
            if (!video.paused) {
              video.pause();
            }
          }
        });
      }, options);

      // Observe all videos
      this.videos.forEach((video) => {
        observer.observe(video);
      });
    }

    disconnectedCallback() {
      // Pause all videos when component is removed
      this.videos.forEach((video) => {
        video.pause();
      });

      // Remove scroll listener
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  customElements.define('media-grid-custom', MediaGridCustom);
}
